// Orders Page

import { useState } from "react";
import {
	useEffect,
	useCallback,
	useSelector,
	useDispatch,
	useROrdersMutation,
	useUOrdersMutation,
	useUCartItemStatusMutation,
	useRCustomersMutation,
} from "custom_exports/all_hooks";
import {
	Box,
	TextField,
	InputAdornment,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Stack,
} from "custom_exports/material_ui_items";
import SearchIcon from "@mui/icons-material/Search";
import {
	orderActions,
	customerActions,
	getOrderItemsAsList,
	getCustomerItemsAsList,
	getPagination,
} from "services/redux/reducers/slice";
import { authSelectors } from "services/redux/reducers";
import ShowStatus from "components/common/ShowStatus";
import { callApi } from "common/api_handlers/api_call_manager";
import OrdersAccordionView from "components/pages/orders/accordion/OrdersAccordionView";
import Swal from "sweetalert2";
import { THEME } from "app/settings/settings";
import K from "common/keywords";
import C from "common/constants";

import Pagination from "components/common/iui/pagination/Pagination";

function OrdersPage(props) {
	//--------------------------------- variables ---------------------------------//
	// ---- initializations ---- //
	// ---- destructuring ---- //
	const { setOrder, updateOrder } = orderActions;

	const { setCustomer } = customerActions;

	// ---- assignments ---- //
	const dispatch = useDispatch();
	const [searchQuery, setSearchQuery] = useState("");
	const [statusFilter, setStatusFilter] = useState("");
	const [ROrdersApiCaller, ROrdersApiResult] = useROrdersMutation();
	const [UOrdersApiCaller, UOrdersApiResult] = useUOrdersMutation();
	const [UCartItemStatusApiCaller, UCartItemStatusApiResult] = useUCartItemStatusMutation();
	const [RCustomersApiCaller, RCustomersApiResult] = useRCustomersMutation();
	const user = useSelector(authSelectors.getUser);
	const ordersAsList = useSelector(getOrderItemsAsList);
	const customersAsList = useSelector(getCustomerItemsAsList);
	const pagination = useSelector(getPagination);

	// Build order statuses list for filter dropdown
	const orderStatusesList = Object.keys(C.orderStatuses)
		.filter((key) => !C.hiddenOrderStatuses.includes(key))
		.map((key) => ({
			title: C.orderStatusLabels[key],
			id: C.orderStatuses[key],
		}));

	//--------------------------------- functions ---------------------------------//
	// ---- api managers ---- //
	const onApiResponse = ({ apiCallerId, ...response }) => {
		const { data, meta_data } = response;
		if (apiCallerId === K.ROrders) {
			dispatch(setOrder({ data, pagination: meta_data.pagination }));
		} else if (apiCallerId === K.UOrders) {
			dispatch(updateOrder(data.orders.items));
		} else if (apiCallerId === K.UCartItemStatus) {
			dispatch(updateOrder(data.orders.items));
		} else if (apiCallerId === K.RCustomers) {
			dispatch(setCustomer(data));
		} else {
			console.log("onApiResponse is not defined for" + apiCallerId);
		}
	};

	const onApiError = ({ apiCallerId, ...error }) => {
		console.log(error);
		if (apiCallerId === K.UOrders) {
			Swal.fire({
				text: "Order Updation Failed, Something Went Wrong..",
				icon: "error",
				confirmButtonColor: THEME.app.colors.red.main,
			});
		} else if (apiCallerId === K.UCartItemStatus) {
			Swal.fire({
				text: "Cart Item Status Update Failed, Something Went Wrong..",
				icon: "error",
				confirmButtonColor: THEME.app.colors.red.main,
			});
		}
	};

	const apiActions = { onApiResponse, onApiError };

	const callROrdersApi = (apiInputParams, apiCallerId) => {
		callApi({
			apiCallerId: apiCallerId ?? K.ROrders,
			apiCaller: ROrdersApiCaller,
			apiInputParams,
			apiActions,
		});
	};

	const callUOrdersApi = (apiInputParams, apiCallerId) => {
		callApi({
			apiCallerId: apiCallerId ?? K.UOrders,
			apiCaller: UOrdersApiCaller,
			apiInputParams,
			apiActions,
		});
	};

	const callUCartItemStatusApi = (apiInputParams, apiCallerId) => {
		callApi({
			apiCallerId: apiCallerId ?? K.UCartItemStatus,
			apiCaller: UCartItemStatusApiCaller,
			apiInputParams,
			apiActions,
		});
	};

	const callRCustomersApi = (apiInputParams, apiCallerId) => {
		callApi({
			apiCallerId: apiCallerId ?? K.RCustomers,
			apiCaller: RCustomersApiCaller,
			apiInputParams,
			apiActions,
		});
	};

	const getFilteredOrdersAsList = useCallback(() => {
		let filteredOrdersAsList = [...ordersAsList];

		// Filter by search query (name or email)
		const searchLower = searchQuery.trim().toLowerCase();
		if (searchLower) {
			filteredOrdersAsList = filteredOrdersAsList.filter((order) => {
				const businessName = order.placed_by_user_details?.profile?.business_name || "";
				const businessEmail = order.placed_by_user_details?.profile?.business_email || "";
				return (
					businessName.toLowerCase().includes(searchLower) ||
					businessEmail.toLowerCase().includes(searchLower)
				);
			});
		}

		// Filter by status
		if (statusFilter !== "") {
			filteredOrdersAsList = filteredOrdersAsList.filter(
				(order) => order.status === statusFilter
			);
		}

		filteredOrdersAsList.sort(
			(a, b) => Date.parse(b.created_on) - Date.parse(a.created_on)
		);

		return filteredOrdersAsList;
	}, [ordersAsList, searchQuery, statusFilter]);

	// ---- handlers ---- //
	const handleOnClick = (id, data) => {
		if (id === K.UOrderStatus) {
			const apiParams = {
				method: "update_orders_statuses",
				id: data.orderId,
				status: data.newStatusId,
			};
			// Include notify_customer if provided (for backorder notifications)
			if (data.notifyCustomer !== undefined) {
				apiParams.notify_customer = data.notifyCustomer;
			}
			callUOrdersApi(apiParams);
		} else if (id === K.UCartItemStatus) {
			const apiParams = {
				cart_item_id: data.cartItemId,
				status: data.newStatusId,
			};
			// Include notify_customer if provided (for backorder notifications)
			if (data.notifyCustomer !== undefined) {
				apiParams.notify_customer = data.notifyCustomer;
			}
			callUCartItemStatusApi(apiParams);
		} else {
			console.log("unknown click id " + id);
		}
	};

	// ---- useEffects ---- //
	useEffect(() => {
		callROrdersApi(); // Always fetch on mount
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (
			user?.role &&
			user.role !== C.user_roles.customer &&
			customersAsList.length <= 0
		) {
			callRCustomersApi();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user]);

	//--------------------------------- contents ----------------------------------//
	const loadingIndication = (
		<ShowStatus
			statusType="loading"
			extraSX={{ bgcolor: "white", zIndex: 1000 }}
		/>
	);

	const filteredOrders = getFilteredOrdersAsList();

	const content = (
		<>
			{ROrdersApiResult.isLoading && loadingIndication}
			{/* Page Container with light grey background */}
			<Box
				sx={{
					bgcolor: "#f8fafc",
					minHeight: "100vh",
					p: { xs: 2, sm: 3 },
				}}
			>
				{/* Search Bar - Rounded white card */}
				<Box
					sx={{
						bgcolor: "white",
						borderRadius: "24px",
						boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
						p: 2,
						px: { xs: 2, sm: 3 },
						mb: 3,
					}}
				>
					<Stack
						direction={{ xs: "column", sm: "row" }}
						spacing={2}
						alignItems={{ xs: "stretch", sm: "center" }}
					>
						<TextField
							size="small"
							sx={{
								width: { xs: "100%", sm: "300px" },
								"& .MuiOutlinedInput-root": {
									borderRadius: "20px",
									bgcolor: "#f1f5f9",
									"& fieldset": {
										border: "none",
									},
									"&:hover fieldset": {
										border: "none",
									},
									"&.Mui-focused fieldset": {
										border: "2px solid #6bc637",
									},
								},
							}}
							variant="outlined"
							placeholder="Search by name or email..."
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<SearchIcon sx={{ color: "grey.500" }} />
									</InputAdornment>
								),
							}}
						/>
						<FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 150 } }}>
							<InputLabel>Status</InputLabel>
							<Select
								value={statusFilter}
								label="Status"
								onChange={(e) => setStatusFilter(e.target.value)}
								sx={{
									borderRadius: "20px",
									bgcolor: "#f1f5f9",
									"& .MuiOutlinedInput-notchedOutline": {
										border: "none",
									},
									"&:hover .MuiOutlinedInput-notchedOutline": {
										border: "none",
									},
									"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
										border: "2px solid #6bc637",
									},
								}}
							>
								<MenuItem value="">All</MenuItem>
								{orderStatusesList.map((status) => (
									<MenuItem key={status.id} value={status.id}>
										{status.title.charAt(0).toUpperCase() +
											status.title.slice(1)}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Stack>
				</Box>

				{/* Orders List */}
				<OrdersAccordionView
					orders={filteredOrders}
					handleOnClick={handleOnClick}
				/>

				{/* Pagination */}
				<Pagination
					style={{ justifyContent: "flex-end", mt: 2, px: 1 }}
					totalRecords={pagination.totalRecords}
					totalPages={pagination.totalPages}
					currentPageNumber={pagination.pageNumber}
					currentDataLength={filteredOrders.length}
					pageSize={pagination.pageSize}
					onPageChange={(pageNumber) =>
						callROrdersApi({ page_number: pageNumber })
					}
					dataName="orders"
				/>
			</Box>
		</>
	);

	return content;
}

export default OrdersPage;
